import PropTypes from 'prop-types'
import { ModalMediator } from './mediator'
import { getDateFormatted, getTime } from '@shared/utils'
import { ModalProvider } from './provider'
import { APPOINTMENT_TYPES } from '@shared/data-access'
import { getDefaultModality } from './common'
import { useNavigate } from 'react-router-dom'
import { ROUTE_PATH } from 'apps/social-referent/src/routes/enums'

export const ModalAgenda = ({
  event,
  dateSelected,
  onClose,
  isReadOnly = false,
  isCoordinator,
  actionAfterSubmit,
  isEdit: isPropEdit = false,
  onGenerateRoom,
  onDeleteAppointmentCallback,
  showDeletedOk,
}) => {
  const {
    appointmentType: { reference } = {
      reference: APPOINTMENT_TYPES.CONSULTATION,
    },
    start,
    end,
    unavailableSlotType,
    id,
    meetingUrl,
    policy,
    personId,
    socialReferentId,
    appointmentNeedsTrackingChange,
    appointmentModality,
    isAppointmentManagerEdit,
    isPostCs,
    isTravelTime = false,
    travelType = '',
    originalAppointmentId = '',
    originalAppointmentStart,
    originalAppointmentEnd,
    currentEffectiveStart,
    currentEffectiveEnd,
  } = event
  const navigate = useNavigate()


  const {
    noDigital,
    id: policyId,
    managerOfficeDirection,
    street,
    officeControl,
  } = policy || {}

  const isEdit = isPropEdit || isAppointmentManagerEdit
  const appointmentSocialReferentId =
    socialReferentId || policy?.socialReferent?.id || ''

  // Handle travel time data differently
  const defaultDate = isTravelTime 
    ? (start ? new Date(start) : dateSelected)
    : (start ? getDateFormatted(start, 'YYYY-MM-DD') : getDateFormatted(dateSelected, 'YYYY-MM-DD'))

  const defaultStartTime = getTime(start)
  const defaultEndTime = getTime(end)

  // For travel time, use a default appointment type number, not string
  const appointmentTypeValue = isTravelTime ? 0 : (typeof reference === 'string' ? 0 : reference)

  const modality =
    appointmentModality || appointmentModality === 0
      ? appointmentModality
      : getDefaultModality({ type: appointmentTypeValue, noDigital })

  const actionAfterSubmitAppointmentManager = () => {
    navigate(`${ROUTE_PATH.userArea(policyId)}`)
  }

  return (
    <ModalProvider
      eventId={id}
      policyId={policyId}
      personId={personId}
      appointmentType={appointmentTypeValue}
      appointmentDay={defaultDate}
      unavailableSlotType={unavailableSlotType}
      startTime={defaultStartTime}
      endTime={defaultEndTime}
      modality={modality}
      meetingUrl={meetingUrl}
      appointmentNeedsTrackingChange={appointmentNeedsTrackingChange}
      onGenerateRoom={onGenerateRoom}
      noDigital={noDigital}
      managerOfficeDirection={managerOfficeDirection}
      street={street}
      officeControl={officeControl}
      isTravelTime={isTravelTime}
      travelType={travelType}
      originalAppointmentId={originalAppointmentId || id}
      originalAppointmentStart={originalAppointmentStart || ''}
      originalAppointmentEnd={originalAppointmentEnd || ''}
      currentEffectiveStart={currentEffectiveStart || ''}
      currentEffectiveEnd={currentEffectiveEnd || ''}
      socialReferentId={appointmentSocialReferentId}
      onDeleteAppointmentCallback={onDeleteAppointmentCallback}  
      showDeletedOk={showDeletedOk}
    >
      <ModalMediator
        isReadOnly={isReadOnly}
        isCoordinator={isCoordinator}
        isEdit={isEdit}
        actionAfterSubmit={
          isAppointmentManagerEdit || isPostCs
            ? actionAfterSubmitAppointmentManager
            : actionAfterSubmit
        }
        onClose={onClose}
      />
    </ModalProvider>
  )
}

ModalAgenda.propTypes = {
  event: PropTypes.shape({
    appointmentType: PropTypes.shape({
      reference: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    start: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.number,
    ]),
    end: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.number,
    ]),
    unavailableSlotType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meetingUrl: PropTypes.string,
    policy: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      socialReferent: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      noDigital: PropTypes.bool,
      managerOfficeDirection: PropTypes.string,
      street: PropTypes.string,
      officeControl: PropTypes.string,
    }),
    personId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    socialReferentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    appointmentNeedsTrackingChange: PropTypes.bool,
    appointmentModality: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isAppointmentManagerEdit: PropTypes.bool,
    isPostCs: PropTypes.bool,
    isTravelTime: PropTypes.bool,
    travelType: PropTypes.string,
    originalAppointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    originalAppointmentStart: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
    originalAppointmentEnd: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
    currentEffectiveStart: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
    currentEffectiveEnd: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
  }).isRequired,
  dateSelected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]).isRequired,
  onClose: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  isCoordinator: PropTypes.bool,
  actionAfterSubmit: PropTypes.func,
  isEdit: PropTypes.bool,
  onGenerateRoom: PropTypes.func,
  onDeleteAppointmentCallback: PropTypes.func,
  showDeletedOk: PropTypes.bool,
}
